import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Newspaper } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { markSubscribed, useSparkle } from "@/lib/sparkle";
import { trackEvent } from "@/hooks/useArticleAnalytics";
import { Micro } from "@/components/layernav/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const DailyBriefSignup = ({ sourceType = "homepage_daily_brief", interests = ["daily_brief"], className = "mt-10 sm:mt-14", stacked = false, subscribed = false }: {
  sourceType?: string; interests?: string[]; className?: string; stacked?: boolean; subscribed?: boolean;
}) => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const sparkle = useSparkle();
  const location = useLocation();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/leads/capture", {
        email, source_type: sourceType, source_page: location.pathname,
        interests, referrer: document.referrer || null, consent: true,
      });
      setDone(true);
      markSubscribed();
      trackEvent("daily_brief_signup", { source: sourceType });
      toast.success("You're in. The Brief lands tomorrow morning.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="daily-brief-signup" data-track="daily_brief"
      className={`relative rounded-xl border border-[#A955FF]/40 bg-[#090614] px-5 sm:px-8 py-8 sm:py-9 overflow-hidden shadow-[0_0_50px_-22px_rgba(169,85,255,0.4)] ${className}`}>
      <div aria-hidden className="absolute -bottom-28 -left-16 h-72 w-72 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(169,85,255,0.2), transparent 70%)" }} />
      <div aria-hidden className="absolute -top-24 -right-16 h-64 w-64 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(16,215,242,0.08), transparent 70%)" }} />
      <div className={`relative flex flex-col gap-6 ${stacked ? "" : "lg:flex-row lg:items-center lg:gap-10"}`}>
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <span aria-hidden className="hidden sm:flex h-[52px] w-[52px] shrink-0 rounded-lg border border-[#A955FF]/45 bg-[#A955FF]/10 items-center justify-center text-[#C08BFF]">
            <Newspaper className="h-[22px] w-[22px]" />
          </span>
          <div className="min-w-0">
            <Micro>The Daily Brief</Micro>
            <h2 className="font-heading font-black text-white text-xl sm:text-2xl tracking-tight mt-1.5">
              The onchain economy, before your day starts.
            </h2>
            <p className="text-gray-400 text-sm mt-1.5 leading-relaxed">
              Curated markets, onchain insights, and key headlines — delivered every weekday morning.
            </p>
          </div>
        </div>
        <div className={`w-full shrink-0 ${stacked ? "" : "lg:w-auto lg:min-w-[400px]"}`}>
          {subscribed && !done ? (
            <div data-testid="daily-brief-subscribed-state">
              <p className="text-[#27e7a6] font-mono text-sm">✓ You're subscribed. Next brief: weekday morning.</p>
              <Link to="/account" data-testid="daily-brief-manage"
                className="inline-flex items-center text-[12.5px] font-bold text-[#10D7F2] hover:text-white transition-colors mt-2">
                Manage preferences →
              </Link>
            </div>
          ) : done ? (
            <p data-testid="daily-brief-signup-success" className="text-[#00E5FF] font-mono text-sm">
              ✓ Subscribed. Intelligence incoming.
            </p>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2.5">
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@work.com" data-testid="daily-brief-email-input"
                className="h-11 text-[14px] bg-[#0d1017] border-[#30364A] text-white placeholder:text-gray-500 focus-visible:ring-[#10D7F2] sm:min-w-[240px]" />
              <Button type="submit" disabled={busy} data-testid="daily-brief-submit-button"
                className={`h-11 px-6 text-[14px] text-white font-bold bg-gradient-to-r from-[#A955FF] via-[#3B6CF6] to-[#10D7F2] hover:brightness-110 shadow-[0_4px_24px_rgba(107,84,246,0.35)] shrink-0 ${sparkle ? "sparkle-btn" : ""}`}>
                {busy ? "..." : "Get the Brief →"}
              </Button>
            </form>
          )}
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-600 mt-3.5">
            Weekdays · Free · ~5 minute read
          </p>
        </div>
      </div>
    </section>
  );
};
